<template>
  <div class="lg: max-w-screen-xl lg:flex shadow bg-white rounded">
    <div
      class="h-48 lg:h-auto lg:w-48 flex-none bg-cover rounded-t lg:rounded-t-none lg:rounded-l text-center overflow-hidden"
      :style="thumbnailBackground"
    ></div>
    <div class="p-4 flex flex-col justify-between leading-normal w-full">
      <div class="mb-8">
        <p class="text-sm text-gray-600 flex items-center">
          <i class="pr-2" :class="[icons[resource.resourceType].icon, icons[resource.resourceType].color]" />
          {{ resource.resourceType }}
        </p>
        <router-link class="text-blue-700 font-medium text-xl mb-2" :to="detailsPageLink">
          {{ resource.identification.title }}
        </router-link>
        <p class="text-gray-700 text-base">
          {{ stripHtmlTags(resource.identification.abstract) }}
        </p>
      </div>
      <div class="items-center w-full">
        <!--<img class="w-10 h-10 rounded-full mr-4" src="/img/jonathan.jpg" alt="Avatar of Jonathan Reinink">-->
        <div class="text-sm w-full">
          <p class="text-gray-900 leading-none"><span class="text-blue-700">Published By: </span>{{ resource.publisher.name }}</p>
          <p class="text-gray-600">
            <span class="text-blue-700">Last Update: </span>{{ dateString(resource.identification.dates.revision) }}
          </p>
        </div>
        <div v-if="canBeAddedToMap" class="w-full justify-end flex flex-row text-right">
          <p class="group px-2 self-center" v-if="timeEnabled">
            <span class="text-gray-600 hover:text-gray-400 cursor-help">
              <i class="far fa-clock text-2xl"></i>
            </span>
            <span class="flex justify-center">
              <span class="tooltip whitespace-nowrap m-2">Time Enabled Layer</span>
            </span>
          </p>

          <button
            v-if="!inCurrentSelection"
            :disabled="addingToSelection"
            type="button"
            class="rounded inline-block px-6 py-2.5 bg-blue-700 text-white font-medium text-xs leading-tight uppercase hover:bg-blue-600 disabled:bg-gray-500"
            @click="addToMap()"
          >
            <div>
              <span v-if="addingToSelection" class="flex justify-center">
                <svg
                  role="status"
                  class="flex absolute w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-700 opacity-50"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  ></path>
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  ></path>
                </svg>
              </span>
              <i class="fas fa-plus pr-2" /> Add to Selection
            </div>
          </button>
          <button
            v-else
            disabled
            type="button"
            class="rounded inline-block px-6 py-2.5 bg-blue-700 text-white font-medium text-xs leading-tight uppercase hover:bg-blue-600 disabled:bg-gray-500"
          >
            Selected
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { computed, ref } from 'vue'
import { ResourceType } from '@/services/common/enums'
import icons from '@/assets/resource-type-icons'
import { dateString } from '@/services/common/date-helpers'
import { stripHtmlTags } from '@/services/common/helpers'

const LayerTypeRouteMap = {
  [ResourceType.VectorLayer]: 'layers',
  [ResourceType.RasterLayer]: 'layers',
  [ResourceType.Table]: 'tables',
  [ResourceType.ExternalApplication]: 'applications',
  [ResourceType.PythonNotebook]: 'notebooks',
  [ResourceType.Scenario]: 'usecases',
}

export default {
  name: 'ListViewItemLayout',
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const addingToSelection = ref(false)

    const reqParams = computed(() => {
      return props.resource.service_information?.requiredParameters
    })

    const detailsPageLink = computed(() => {
      return '/' + LayerTypeRouteMap[props.resource.resourceType] + '/' + props.resource.id
    })
    const thumbnailBackground = computed(() => {
      const tl = props.resource.identification.thumbnailUrl
      if (tl === '') {
        console.log('no thumbs')
      }
      return 'background-image: url(' + tl + ')'
    })

    const canBeAddedToMap = computed(() => {
      return props.resource.resourceType === ResourceType.RasterLayer || props.resource.resourceType === ResourceType.VectorLayer
    })

    const timeEnabled = computed(() => {
      let timeEnabled = Object.values(reqParams.value)?.find((val) => {
        if (val.type === 'timestamp') {
          return true
        }
      })
      return timeEnabled !== undefined
    })

    async function addToMap() {
      if (!inCurrentSelection.value) {
        addingToSelection.value = true
        await store.dispatch('Map/addLayer', props.resource).then(() => {
          addingToSelection.value = false
        })
      }
    }

    const inCurrentSelection = computed(() => {
      const maybeSelected = store.state.Map.layers.find((obj) => {
        return obj.id === props.resource.id
      })
      return maybeSelected !== undefined
    })

    return {
      icons,
      detailsPageLink,
      thumbnailBackground,
      dateString,
      addToMap,
      inCurrentSelection,
      canBeAddedToMap,
      stripHtmlTags,
      timeEnabled,
      addingToSelection,
    }
  },
}
</script>

<style>

</style>
